import jsPDF from 'jspdf';
import { format } from 'date-fns';

export const generateTravelPDF = async (invoice) => {
  try {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 50;
    let y = 0;

    const currencies = {
      USD: { symbol: '$', name: 'US Dollar' },
      EUR: { symbol: '€', name: 'Euro' },
      GBP: { symbol: '£', name: 'British Pound' }
    };

    const getCurrencySymbol = (code) => currencies[code]?.symbol || '$';
    const currencySymbol = getCurrencySymbol(invoice.currency);

    const invoiceNumber = invoice.fullNumber || `TRV-${invoice.number}`;

    const customColors = invoice.colors || {
      primary: '#1e3a8a',
      secondary: '#6366f1',
      accent: '#ec4899',
      dark: '#0f172a',
      light: '#f8fafc'
    };

    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 0, g: 0, b: 0 };
    };

    const colors = {
      primary: hexToRgb(customColors.primary),
      secondary: hexToRgb(customColors.secondary),
      accent: hexToRgb(customColors.accent),
      dark: hexToRgb(customColors.dark),
      light: hexToRgb(customColors.light)
    };

    // --- HEADER WITH GRADIENT ---
    doc.setFillColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.rect(0, 0, pageWidth, 120, 'F');

    y = 25;

    // --- LOGO ---
    if (invoice.companyInfo?.logoUrl) {
      try {
        const img = new Image();
        img.src = invoice.companyInfo.logoUrl;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        
        const maxWidth = 150;
        const maxHeight = 90;
        const imgAspectRatio = img.width / img.height;
        const boxAspectRatio = maxWidth / maxHeight;

        let logoWidth;
        let logoHeight;

        if (imgAspectRatio > boxAspectRatio) {
          logoWidth = maxWidth;
          logoHeight = maxWidth / imgAspectRatio;
        } else {
          logoHeight = maxHeight;
          logoWidth = maxHeight * imgAspectRatio;
        }
        
        doc.addImage(invoice.companyInfo.logoUrl, 'PNG', margin, y, logoWidth, logoHeight);
      } catch (e) {
        console.log('Logo could not be loaded.');
      }
    }

    // --- TRAVEL INVOICE TITLE ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(36);
    doc.setTextColor(255, 255, 255);
    doc.text('TRAVEL INVOICE', pageWidth - margin, y + 20, { align: 'right' });

    // --- STATUS LABEL ---
    const status = invoice.status || 'unpaid';
    const isPaid = status === 'paid';
    const statusText = isPaid ? 'PAID' : 'UNPAID';
    const statusColor = isPaid ? [0, 128, 0] : [220, 38, 38];

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
    doc.text(statusText, pageWidth - margin, y + 40, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(`Invoice #: ${invoiceNumber}`, pageWidth - margin, y + 56, { align: 'right' });
    doc.text(`Date: ${format(new Date(invoice.travelDate || invoice.createdAt), 'MMMM dd, yyyy')}`, pageWidth - margin, y + 70, { align: 'right' });

    y = 140;

    // --- COMPANY INFO CARD ---
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin, y, pageWidth - (margin * 2), 80, 8, 8, 'F');
    doc.setDrawColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
    doc.setLineWidth(2);
    doc.roundedRect(margin, y, pageWidth - (margin * 2), 80, 8, 8);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(colors.dark.r, colors.dark.g, colors.dark.b);
    doc.text(invoice.companyInfo?.name || 'Travel Agency', margin + 20, y + 25);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(75, 85, 99);
    let infoY = y + 45;
    if (invoice.companyInfo?.phone) {
      doc.text(`Phone: ${invoice.companyInfo.phone}`, margin + 20, infoY);
      infoY += 15;
    }
    if (invoice.companyInfo?.email) {
      doc.text(`Email: ${invoice.companyInfo.email}`, margin + 20, infoY);
    }

    y += 100;

    // --- CUSTOMER & TRAVEL INFO CARDS ---
    const cardWidth = (pageWidth - (margin * 2) - 20) / 2;
    const cardHeight = 140;

    // Customer card
    doc.setFillColor(colors.light.r, colors.light.g, colors.light.b);
    doc.roundedRect(margin, y, cardWidth, cardHeight, 8, 8, 'F');
    doc.setDrawColor(229, 231, 235);
    doc.roundedRect(margin, y, cardWidth, cardHeight, 8, 8);

    doc.setFillColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
    doc.roundedRect(margin, y, cardWidth, 30, 8, 8, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text('CUSTOMER', margin + 15, y + 18);

    doc.setTextColor(55, 65, 81);
    let customerY = y + 50;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(invoice.customer?.name || 'Customer Name', margin + 15, customerY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    customerY += 18;

    if (invoice.customer?.email) {
      doc.text(invoice.customer.email, margin + 15, customerY);
      customerY += 14;
    }
    if (invoice.customer?.phone) {
      doc.text(invoice.customer.phone, margin + 15, customerY);
      customerY += 14;
    }

    // Travel Details card
    const travelX = margin + cardWidth + 20;

    doc.setFillColor(colors.light.r, colors.light.g, colors.light.b);
    doc.roundedRect(travelX, y, cardWidth, cardHeight, 8, 8, 'F');
    doc.setDrawColor(229, 231, 235);
    doc.roundedRect(travelX, y, cardWidth, cardHeight, 8, 8);

    doc.setFillColor(colors.accent.r, colors.accent.g, colors.accent.b);
    doc.roundedRect(travelX, y, cardWidth, 30, 8, 8, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text('TRAVEL DETAILS', travelX + 15, y + 18);

    doc.setTextColor(55, 65, 81);
    let travelY = y + 50;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Destination:', travelX + 15, travelY);
    doc.setFont('helvetica', 'normal');
    doc.text(invoice.destination || 'N/A', travelX + 90, travelY);

    travelY += 18;
    doc.setFont('helvetica', 'bold');
    doc.text('Travel Date:', travelX + 15, travelY);
    doc.setFont('helvetica', 'normal');
    doc.text(format(new Date(invoice.travelDate || Date.now()), 'MMM dd, yyyy'), travelX + 90, travelY);

    if (invoice.returnDate) {
      travelY += 18;
      doc.setFont('helvetica', 'bold');
      doc.text('Return Date:', travelX + 15, travelY);
      doc.setFont('helvetica', 'normal');
      doc.text(format(new Date(invoice.returnDate), 'MMM dd, yyyy'), travelX + 90, travelY);
    }

    travelY += 18;
    doc.setFont('helvetica', 'bold');
    doc.text('Currency:', travelX + 15, travelY);
    doc.setFont('helvetica', 'normal');
    doc.text(`${invoice.currency} (${currencySymbol})`, travelX + 90, travelY);

    y = y + cardHeight + 40;

    // --- SERVICES TABLE ---
    const tableHeaders = ['Service', 'Qty', 'Price', 'Total'];
    const tableStartX = margin;
    const tableWidth = pageWidth - (margin * 2);
    const colWidths = [tableWidth * 0.5, tableWidth * 0.15, tableWidth * 0.175, tableWidth * 0.175];

    doc.setFillColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.roundedRect(tableStartX, y, tableWidth, 30, 5, 5, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);

    let currentX = tableStartX + 15;
    doc.text(tableHeaders[0], currentX, y + 18);
    currentX += colWidths[0];
    doc.text(tableHeaders[1], currentX + colWidths[1] / 2, y + 18, { align: 'center' });
    currentX += colWidths[1];
    doc.text(tableHeaders[2], currentX + colWidths[2] / 2, y + 18, { align: 'center' });
    currentX += colWidths[2];
    doc.text(tableHeaders[3], currentX + colWidths[3] / 2, y + 18, { align: 'center' });

    y += 30;

    (invoice.items || []).forEach((item, index) => {
      const nameText = item.name || 'Service';
      const descriptionText = item.description || '';
      const nameLines = doc.splitTextToSize(nameText, colWidths[0] - 20);
      const descriptionLines = descriptionText ? doc.splitTextToSize(descriptionText, colWidths[0] - 20) : [];
      const rowHeight = (nameLines.length * 14) + (descriptionLines.length * 12) + 25;

      if (y + rowHeight > pageHeight - 150) {
        doc.addPage();
        y = margin;
      }

      if (index % 2 === 0) {
        doc.setFillColor(colors.light.r, colors.light.g, colors.light.b);
      } else {
        doc.setFillColor(255, 255, 255);
      }
      doc.rect(tableStartX, y, tableWidth, rowHeight, 'F');

      let lineY = y + 15;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(colors.dark.r, colors.dark.g, colors.dark.b);
      doc.text(nameLines, tableStartX + 15, lineY);
      lineY += nameLines.length * 14;

      if (descriptionLines.length > 0) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(9);
        doc.setTextColor(107, 114, 128);
        doc.text(descriptionLines, tableStartX + 15, lineY);
      }

      const quantity = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      const total = quantity * price;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(colors.dark.r, colors.dark.g, colors.dark.b);

      let colX = tableStartX + colWidths[0];
      doc.text(String(quantity), colX + colWidths[1] / 2, y + 20, { align: 'center' });
      colX += colWidths[1];
      doc.text(`${currencySymbol}${price.toFixed(2)}`, colX + colWidths[2] / 2, y + 20, { align: 'center' });
      colX += colWidths[2];
      doc.setFont('helvetica', 'bold');
      doc.text(`${currencySymbol}${total.toFixed(2)}`, colX + colWidths[3] / 2, y + 20, { align: 'center' });

      y += rowHeight;
    });

    y += 20;

    // --- TOTALS SECTION ---
    const subtotal = (invoice.items || []).reduce((sum, item) => (sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0)), 0);
    const totalAmount = subtotal + (parseFloat(invoice.processingFee) || 0);

    const totalsBoxWidth = 250;
    const totalsBoxX = pageWidth - margin - totalsBoxWidth;

    doc.setFillColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
    doc.roundedRect(totalsBoxX, y, totalsBoxWidth, 30, 5, 5, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text('TOTAL AMOUNT', totalsBoxX + totalsBoxWidth / 2, y + 18, { align: 'center' });
    y += 30;

    const totalsContentHeight = invoice.processingFee > 0 ? 80 : 60;
    doc.setFillColor(colors.light.r, colors.light.g, colors.light.b);
    doc.roundedRect(totalsBoxX, y, totalsBoxWidth, totalsContentHeight, 5, 5, 'F');

    let totalY = y + 25;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(55, 65, 81);
    doc.text('Subtotal:', totalsBoxX + 20, totalY);
    doc.text(`${currencySymbol}${subtotal.toFixed(2)}`, totalsBoxX + totalsBoxWidth - 20, totalY, { align: 'right' });

    if (invoice.processingFee > 0) {
      totalY += 20;
      doc.text('Service Fee:', totalsBoxX + 20, totalY);
      doc.text(`${currencySymbol}${invoice.processingFee.toFixed(2)}`, totalsBoxX + totalsBoxWidth - 20, totalY, { align: 'right' });
    }

    totalY += 25;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(colors.accent.r, colors.accent.g, colors.accent.b);
    doc.text('TOTAL:', totalsBoxX + 20, totalY);
    doc.text(`${currencySymbol}${totalAmount.toFixed(2)}`, totalsBoxX + totalsBoxWidth - 20, totalY, { align: 'right' });

    y += totalsContentHeight + 30;

    // --- NOTES SECTION ---
    if (invoice.notes && invoice.notes.trim()) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(colors.dark.r, colors.dark.g, colors.dark.b);
      doc.text('Additional Notes:', margin, y);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(75, 85, 99);

      const noteLines = doc.splitTextToSize(invoice.notes, pageWidth - (margin * 2));
      noteLines.forEach(line => {
        doc.text(line, margin, y + 15);
        y += 12;
      });

      y += 15;
    }

    // --- FOOTER ---
    const footerY = pageHeight - 40;
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.text('Thank you for choosing our premium travel services!', pageWidth / 2, footerY, { align: 'center' });
    doc.setFontSize(8);
    doc.text('Have a wonderful journey!', pageWidth / 2, footerY + 15, { align: 'center' });

    // --- SAVE ---
    const customerName = (invoice.customer?.name || 'Customer').replace(/[^a-zA-Z0-9]/g, '_');
    const travelDate = format(new Date(invoice.travelDate || Date.now()), 'yyyy-MM-dd');
    const filename = `${customerName}_travel_invoice_${invoiceNumber.replace('-', '_')}_${travelDate}.pdf`;

    doc.save(filename);

  } catch (error) {
    console.error('Error generating travel PDF:', error);
    throw new Error(`Failed to generate PDF: ${error.message}`);
  }
};